import styled from 'styled-components';

export const GuideBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  background: #edf0ff;
  color: #5533cf;
  font-size: 12px;
  font-weight: 600;
  padding: 5px 12px;
  border-radius: 9999px;
`;

export const StyledFigure = styled.figure`
  margin: 18px 0 0;
  border: 1px solid ${({ theme }) => theme.colors.cardBorder};
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(16, 24, 40, 0.1);
`;

export const FigureImage = styled.img`
  display: block;
  width: 100%;
  height: auto;
`;

export const FigureCaption = styled.figcaption`
  padding: 9px 14px;
  font-size: 12.5px;
  color: #667085;
  background: #f9fafb;
  border-top: 1px solid ${({ theme }) => theme.colors.cardBorder};
`;

export const BulletList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 9px;
`;

export const BulletItemWrapper = styled.li`
  display: flex;
  gap: 10px;
  font-size: 15px;
  line-height: 1.55;
  color: ${({ theme }) => theme.colors.cardBox};
`;

interface BulletDotProps {
  $color: string;
}

export const BulletDot = styled.span<BulletDotProps>`
  flex-shrink: 0;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: ${({ $color }) => $color};
  margin-top: 8px;
`;

export const CheckList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const CheckItemWrapper = styled.li`
  display: flex;
  gap: 10px;
  font-size: 15px;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.cardBox};
`;

export const GuideDivider = styled.hr`
  border: none;
  border-top: 1px solid ${({ theme }) => theme.colors.cardBorder};
  margin: 48px 0;
`;

// ── Timeline ───────────────────────────────────────────────────────────────────

export const TimelineWrapper = styled.div`
  display: flex;
  gap: 22px;
`;

export const TimelineLeft = styled.div`
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

interface TimelineBadgeProps {
  $dark: boolean;
}

export const TimelineBadge = styled.span<TimelineBadgeProps>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: ${({ $dark, theme }) => ($dark ? '#101828' : theme.colors.bopsPurple)};
  color: #fff;
  font-size: 14px;
  font-weight: 700;
`;

export const TimelineLine = styled.span`
  flex: 1;
  width: 2px;
  background: ${({ theme }) => theme.colors.cardBorder};
  margin: 8px 0;
`;

interface TimelineContentProps {
  $last: boolean;
}

export const TimelineContent = styled.div<TimelineContentProps>`
  flex: 1;
  padding-bottom: ${({ $last }) => ($last ? '0' : '40px')};
  min-width: 0;
`;

export const TimelineTitle = styled.h3`
  font-size: 17px;
  font-weight: 600;
  color: #101828;
  margin: 4px 0 8px;
`;

// ── Body text ──────────────────────────────────────────────────────────────────

interface BodyTextProps {
  $margin?: string;
}

export const BodyText = styled.p<BodyTextProps>`
  font-size: 15px;
  line-height: 1.6;
  color: ${({ theme }) => theme.colors.cardBox};
  margin: ${({ $margin }) => $margin ?? '0'};
`;

// ── Prereqs section ────────────────────────────────────────────────────────────

export const PrereqsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  padding-left: 44px;
`;

export const PrereqCard = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.cardBorder};
  border-radius: 12px;
  padding: 22px;
`;

export const PrereqIconWrap = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 9px;
  background: #f9fafb;
  color: ${({ theme }) => theme.colors.cardBox};
  margin-bottom: 14px;
`;

export const PrereqTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: #101828;
  margin: 0 0 6px;
`;

export const PrereqBody = styled.p`
  font-size: 14px;
  line-height: 1.55;
  color: #667085;
  margin: 0;
`;

export const InlineCode = styled.code`
  font-family: ui-monospace, Menlo, monospace;
  font-size: 12.5px;
  background: #f9fafb;
  border: 1px solid ${({ theme }) => theme.colors.cardBorder};
  padding: 1px 6px;
  border-radius: 5px;
  color: #5533cf;
`;

// ── Form table ─────────────────────────────────────────────────────────────────

export const FormTable = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.cardBorder};
  border-radius: 12px;
  overflow: hidden;
`;

export const FormTableGrid = styled.div`
  display: grid;
  grid-template-columns: 190px 1fr;
  font-size: 14px;
`;

interface FormTableCellProps {
  $last: boolean;
}

export const FormTableLabel = styled.div<FormTableCellProps>`
  padding: 11px 16px;
  background: #f9fafb;
  font-weight: 600;
  color: #101828;
  border-bottom: ${({ $last, theme }) => ($last ? 'none' : `1px solid ${theme.colors.cardBorder}`)};
`;

export const FormTableValue = styled.div<FormTableCellProps>`
  padding: 11px 16px;
  color: ${({ theme }) => theme.colors.cardBox};
  border-bottom: ${({ $last, theme }) => ($last ? 'none' : `1px solid ${theme.colors.cardBorder}`)};
`;

// ── Register section ───────────────────────────────────────────────────────────

export const IndentedSection = styled.div`
  padding-left: 44px;
`;

export const StepList = styled.ol`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

export const StepItem = styled.li`
  display: flex;
  gap: 14px;
`;

export const StepItemContent = styled.div`
  flex: 1;
`;

export const StepBody = styled.p`
  font-size: 15px;
  line-height: 1.6;
  color: ${({ theme }) => theme.colors.cardBox};
  margin: 1px 0 0;
`;

export const NumBadge = styled.span`
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background: #edf0ff;
  color: #5533cf;
  font-size: 13px;
  font-weight: 700;
`;

export const Callout = styled.div`
  display: flex;
  gap: 14px;
  margin-top: 24px;
  background: #fffaeb;
  border: 1px solid #fedf89;
  border-radius: 12px;
  padding: 18px 20px;
`;

export const CalloutTitle = styled.div`
  font-size: 14px;
  font-weight: 700;
  color: #93370d;
  margin-bottom: 3px;
`;

export const CalloutBody = styled.p`
  font-size: 14.5px;
  line-height: 1.55;
  color: #7a2e0e;
  margin: 0;
`;

// ── Figure grid ────────────────────────────────────────────────────────────────

export const FigureGrid = styled.div`
  display: grid;
  gap: 14px;
  margin-top: 18px;
`;
